/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react'
import { Grid, Typography } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import ShowAverageDispatchClosingTime from './components'
import { useHookstate } from '@hookstate/core'
import type { IDespacthStatisticGraphProps } from '../../interfaces/IDispacthStatistic'
import { useMarkerState } from "~/features/Map/stores/MapState";
import {
  getAverageDispatchFinishTime,
  getAverageDispatchTime,
  getAverageDispatchTotalTime,
  getAverageDisplacementTime,
} from './services'

type PayloadProps = {
  readonly withSolvedThirdParty?: boolean
  readonly exludeClosed?: boolean
}

interface TimePros {
  readonly name?: string
  readonly time?: string | null
  readonly loadingInfo?: boolean
}

export interface AverageTimesProps {
  readonly AverageDispatchTotalTime: TimePros
  readonly AverageDispatchTime: TimePros
  readonly AverageDisplacementTime: TimePros
  readonly AverageDispatchFinishTime: TimePros
}

interface changeLoadingAveragesProps {
  readonly finishRequest: boolean
  readonly averageName?: string
}

// eslint-disable-next-line max-lines-per-function
export default function AverageDispatchClosingTime({
  loading,
  searched,
  agencyId,
  deviceId,
  operationId,
  dispatchGroupId,
  category,
  type,
  subtype,
  dateStart,
  dateFinish,
  thirdParty,
  refresh,
  consummated,
  statusIds,
  cityIds,
  groupSetId,
  location,
  operationUnitId,
  closedStatusClassId,
  closedStatusId,
  qualifierOptions,
  expandFilterSearch,
  phone,
  requester,
  userId,
  districtIds,
  geographicalArea,
  startTime,
  endTime,
}: IDespacthStatisticGraphProps) {
  const { translate } = useLang()
  const averageTimes = useHookstate<AverageTimesProps | null>(null)
  const payload = useHookstate<PayloadProps>({
    withSolvedThirdParty: thirdParty,
    exludeClosed: false,
  })

  const changeLoadingAverages = ({
    finishRequest,
    averageName,
  }: changeLoadingAveragesProps) => {
    if (!finishRequest) {
      averageTimes.merge({
        AverageDispatchTotalTime: {
          loadingInfo: true,
        },
        AverageDispatchFinishTime: {
          loadingInfo: true,
        },
        AverageDispatchTime: {
          loadingInfo: true,
        },
        AverageDisplacementTime: {
          loadingInfo: true,
        },
      })
    } else {
      switch (averageName) {
        case 'AverageDispatchTotalTime':
          averageTimes.merge((prev) => ({
            AverageDispatchTotalTime: {
              ...prev.AverageDispatchTotalTime,
              loadingInfo: false,
            },
          }))
          break
        case 'AverageDispatchFinishTime':
          averageTimes.merge((prev) => ({
            AverageDispatchFinishTime: {
              ...prev.AverageDispatchFinishTime,
              loadingInfo: false,
            },
          }))
          break
        case 'AverageDispatchTime':
          averageTimes.merge((prev) => ({
            AverageDispatchTime: {
              ...prev.AverageDispatchTime,
              loadingInfo: false,
            },
          }))
          break
        case 'AverageDisplacementTime':
          averageTimes.merge((prev) => ({
            AverageDisplacementTime: {
              ...prev.AverageDisplacementTime,
              loadingInfo: false,
            },
          }))
          break
      }
    }
  }

  useEffect(() => {
    payload.set({ withSolvedThirdParty: thirdParty, exludeClosed: false })
    changeLoadingAverages({ finishRequest: false })

    getAverageDispatchTotalTime({
      agencyId,
      deviceId,
      dispatchGroupId: dispatchGroupId,
      operationId: operationId,
      categoryIds: category,
      typeIds: type,
      subtypeIds: subtype,
      startDate: dateStart,
      finishDate: dateFinish,
      payload: payload.get(),
      consummated: consummated,
      statusIds: statusIds,
      cityIds: cityIds,
      districtIds: districtIds,
      groupSetId: groupSetId,
      location: location,
      closedStatusClassId: closedStatusClassId,
      closedStatusId: closedStatusId,
      qualifierOptions: qualifierOptions,
      operationUnitId: operationUnitId,
      phone: phone,
      geographicAreas: geographicalArea,
      name: requester,
      userId: userId,
      startTime: startTime,
      endTime: endTime,
    })
      .then((response) => {
        averageTimes.merge({
          AverageDispatchTotalTime: {
            name: translate('Average Dispatch Total Time'),
            time: response,
          },
        })
      })
      .finally(() => {
        changeLoadingAverages({
          finishRequest: true,
          averageName: 'AverageDispatchTotalTime',
        })
      })

    getAverageDispatchTime({
      agencyId,
      deviceId,
      dispatchGroupId: dispatchGroupId,
      operationId: operationId,
      categoryIds: category,
      typeIds: type,
      subtypeIds: subtype,
      startDate: dateStart,
      finishDate: dateFinish,
      payload: payload.get(),
      consummated: consummated,
      statusIds: statusIds,
      cityIds: cityIds,
      districtIds: districtIds,
      groupSetId: groupSetId,
      location: location,
      closedStatusClassId: closedStatusClassId,
      closedStatusId: closedStatusId,
      qualifierOptions: qualifierOptions,
      operationUnitId: operationUnitId,
      phone: phone,
      geographicAreas: geographicalArea,
      name: requester,
      userId: userId,
      startTime: startTime,
      endTime: endTime,
    })
      .then((response) => {
        averageTimes.merge({
          AverageDispatchTime: {
            name: translate('Average Dispatch Time'),
            time: response,
          },
        })
      })
      .finally(() => {
        changeLoadingAverages({
          finishRequest: true,
          averageName: 'AverageDispatchTime',
        })
      })

    getAverageDisplacementTime({
      agencyId,
      deviceId,
      dispatchGroupId: dispatchGroupId,
      operationId: operationId,
      categoryIds: category,
      typeIds: type,
      subtypeIds: subtype,
      startDate: dateStart,
      finishDate: dateFinish,
      payload: payload.get(),
      consummated: consummated,
      statusIds: statusIds,
      cityIds: cityIds,
      districtIds: districtIds,
      groupSetId: groupSetId,
      location: location,
      closedStatusClassId: closedStatusClassId,
      closedStatusId: closedStatusId,
      qualifierOptions: qualifierOptions,
      operationUnitId: operationUnitId,
      phone: phone,
      geographicAreas: geographicalArea,
      name: requester,
      userId: userId, 
      startTime: startTime,
      endTime: endTime,
    })
      .then((response) => {
        averageTimes.merge({
          AverageDisplacementTime: {
            name: translate('Average Displacement Time'),
            time: response,
          },
        })
      })
      .finally(() => {
        changeLoadingAverages({
          finishRequest: true,
          averageName: 'AverageDisplacementTime',
        })
      })

    getAverageDispatchFinishTime({
      agencyId,
      deviceId,
      dispatchGroupId: dispatchGroupId,
      operationId: operationId,
      categoryIds: category,
      typeIds: type,
      subtypeIds: subtype,
      startDate: dateStart,
      finishDate: dateFinish,
      payload: payload.get(),
      consummated: consummated,
      statusIds: statusIds,
      cityIds: cityIds,
      districtIds: districtIds,
      groupSetId: groupSetId,
      location: location,
      closedStatusClassId: closedStatusClassId,
      closedStatusId: closedStatusId,
      qualifierOptions: qualifierOptions,
      operationUnitId: operationUnitId,
      phone: phone,
      geographicAreas: geographicalArea,
      name: requester,
      userId: userId,
      startTime: startTime,
      endTime: endTime,
    })
      .then((response) => {
        averageTimes.merge({
          AverageDispatchFinishTime: {
            name: translate('Average Dispatch Finish Time'),
            time: response,
          },
        })
      })
      .finally(() => {
        changeLoadingAverages({
          finishRequest: true,
          averageName: 'AverageDispatchFinishTime',
        })
      })
  }, [refresh])

  useEffect(() => {
    if (!searched) {
      averageTimes.set(null)
    }
  }, [searched])

  return (
    <Grid
      container
      xs={12}
      sx={{ display: 'flex', paddingX: 3, paddingTop: 3 }}
    >
      <Grid item xs={12} hidden={!expandFilterSearch}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
          {translate('Indicators by average time')}
        </Typography>
        <ShowAverageDispatchClosingTime averageTime={averageTimes.get()} />
      </Grid>
    </Grid>
  )
}
